import { computed, makeObservable, observable, runInAction } from "mobx";
import { BaasicUserStore } from "mrb/baasic";
import { ProducerUserAccountService, UserSettingsService, CertifierDashboardService } from "common/services";
import { isNil, some, get, has, isArray, split, first, startsWith, includes } from "lodash";
import { nakiRadioSystemTypes, nakiRadioUserAccessType } from "common/constants";

import ReactGA from "react-ga4";

class NakiRadioUserStore extends BaasicUserStore {
    @observable producerType = null;
    @observable _isCertifier = false;
    @observable hideTrending = false;

    @computed get isAdministrator() {
        if (this.user && this.user.roles) {
            return some(this.user.roles, (roleName) => roleName === "Administrators");
        }
        return false;
    }

    @computed get isProducer() {
        if (this.user && this.user.roles) {
            return some(this.user.roles, (roleName) => roleName === "Producers");
        }
        return false;
    }

    @computed get isLoadio() {
        if (this.user && this.user.roles) {
            return some(this.user.roles, (roleName) => roleName === "Loadio");
        }
        return false;
    }

    @computed get isLoadioOnly() {
        return this.isLoadio && !this.isAdministrator;
    }

    @computed get isContentPolicyVisible() {
        return this.user && this.user.userProfile.membership.contentPolicyVisible !== false;
    }

    @computed get isTorahAnytimeAvailabilityVisible() {
        return this.user && this.user.userProfile.membership.torahAnytimeAvailabilityVisible !== false;
    }

    @computed get isConnectDeviceMessageVisible() {
        return this.user && this.user.userProfile.membership.connectDeviceVisible !== false;
    }

    @computed get isContentUnavailableInAppPolicyVisible() {
        return this.user && this.user.userProfile.membership.contentUnavailableInAppVisible !== false;
    }

    @computed get isCertifier() {
        if (this.user && this.user.roles) {
            if (some(this.user.roles, (roleName) => roleName === "Certifiers" || roleName === "Administrators")) {
                return true;
            }
        }
        return this._isCertifier === true;
    }

    @computed get isTrendingHidden() {
        return this.hideTrending === true;
    }

    _userSettingsService = null;
    _producerUserAccountService = null;
    _certifierDashboardService = null;

    get userSettingsService() {
        if (isNil(this._userSettingsService)) {
            this._userSettingsService = this.rootStore.createApplicationService(UserSettingsService);
        }
        return this._userSettingsService;
    }

    get producerUserAccountService() {
        if (isNil(this._producerUserAccountService)) {
            this._producerUserAccountService = this.rootStore.createApplicationService(ProducerUserAccountService);
        }
        return this._producerUserAccountService;
    }

    get certifierDashboardService() {
        if (isNil(this._certifierDashboardService)) {
            this._certifierDashboardService = this.rootStore.createApplicationService(CertifierDashboardService);
        }
        return this._certifierDashboardService;
    }

    constructor(rootStore) {
        super(rootStore);
        makeObservable(this);
    }

    async resolveUser() {
        await super.resolveUser();
        if (this.isAdministrator) {
            this.rootStore.routerStore.setSystemRoute("initialState", "master.administration.dashboard");
        } else if (this.isLoadio) {
            this.rootStore.routerStore.setSystemRoute(
                "initialState",
                "master.administration.loadio-dashboard.profile.list"
            );
        } else {
            await this.rootStore.premiumSubscriptionStore.refreshUserPremiumSubscription();
            if (this.isProducer) {
                this.rootStore.routerStore.setSystemRoute(
                    "initialState",
                    "master.application.producer.preview.dashboard"
                );
            } else if (this.rootStore.premiumSubscriptionStore.hasPortalAccess) {
                // Land device-less users on the Devices area (which shows onboarding
                // directions); users with a device keep the Stories default.
                const deviceCount = await this.rootStore.userDeviceStore.fetchDeviceCount();
                const landingRoute =
                    deviceCount === 0
                        ? "master.application.device.list"
                        : "master.application.categories.stories";
                this.rootStore.routerStore.setSystemRoute("initialState", landingRoute);
            } else {
                this.rootStore.routerStore.setSystemRoute("initialState", "master.application.premium.subscription");
            }
            ReactGA.set({ user_id: this.user?.id });
        }

        if (this.isProducer) {
            var result = await this.producerUserAccountService.getProducerUserAccountProducerType();
            var producerType = result.data;
            runInAction(() => {
                this.producerType = producerType;
            });
        }

        if (this.isCertifier && !this.isAdministrator) {
            try {
                var modesResult = await this.certifierDashboardService.findCertifierModes({ page: 1, rpp: 1 });
                runInAction(() => {
                    this._isCertifier = modesResult.data && modesResult.data.totalRecords > 0;
                });
            } catch {
                runInAction(() => {
                    this._isCertifier = false;
                });
            }
        }

        try {
            const hideTrendingResult = await this.userSettingsService.getHideTrending();
            runInAction(() => {
                this.hideTrending = hideTrendingResult.data && hideTrendingResult.data.hideTrending === true;
            });
        } catch {
            runInAction(() => {
                this.hideTrending = false;
            });
        }
    }

    async disableContentPolicy() {
        await this.userSettingsService.disableContentPolicy();
        await this.rootStore.userStore.refreshUserAccount();
    }

    async disableTorahAnytimeAvailability() {
        await this.userSettingsService.disableTorahAnytimeAvailability();
        await this.rootStore.userStore.refreshUserAccount();
    }

    async disableConnectDeviceMessage() {
        await this.userSettingsService.disableConnectDevice();
        await this.rootStore.userStore.refreshUserAccount();
    }

    async disableContentUnavailableInAppPolicy() {
        await this.userSettingsService.disableContentUnavailableInAppPolicy();
        await this.rootStore.userStore.refreshUserAccount();
    }

    async updateHideTrending(value) {
        const previousValue = this.hideTrending;
        try {
            await this.userSettingsService.updateHideTrending(value);
            runInAction(() => {
                this.hideTrending = value;
            });
        } catch (err) {
            runInAction(() => {
                this.hideTrending = previousValue;
            });
            throw err;
        }
    }

    async updateAccountInfo(data) {
        await this.rootStore.application.baasic.membershipModule.user.update(data);
        await this.refreshUserAccount();
    }

    hasPermission(authorization) {
        const permissionFunc = (policy) => {
            if (authorizationValid(policy) && has(this.permissions, policy)) {
                return get(this.permissions, policy);
            }
            if (policy && startsWith(policy, "$certifier$")) {
                return this.isCertifier;
            } else if (policy && startsWith(policy, "$premium-only$")) {
                const hasPermission = this.rootStore.premiumSubscriptionStore.hasPortalAccess;

                if (includes(String(authorization), "$not-mobile-restricted$")) {
                    const selectedDeviceSystemType = this.rootStore.userDeviceStore?.selectedDevice?.device?.systemType?.abrv;
                    return hasPermission && selectedDeviceSystemType !== nakiRadioSystemTypes.mobile_restricted && selectedDeviceSystemType !== nakiRadioSystemTypes.mobile_handheld;
                }

                return hasPermission;
            } else if (startsWith(authorization, "$new-user-only$")) {
                const hasPermission =
                    this.rootStore.premiumSubscriptionStore.nakiRadioUserAccessType === nakiRadioUserAccessType.premium;

                const hasSubscription = this.rootStore.premiumSubscriptionStore.hasPortalAccess;

                if (includes(String(authorization), "$no-portal-access$")) {
                    return hasPermission && !hasSubscription;
                }

                if (includes(String(authorization), "$has-portal-access$")) {
                    return hasPermission && hasSubscription;
                }

                return hasPermission;
            } else if (startsWith(authorization, "$old-user-only$")) {
                const hasPermission =
                    this.rootStore.premiumSubscriptionStore.nakiRadioUserAccessType === nakiRadioUserAccessType.free;

                return hasPermission;
            } else {
                const { user } = this.rootStore.userStore;
                const hasPermission = this.permissionService.hasPermission(user, policy);

                if (authorizationValid(policy)) {
                    this.permissions[policy] = hasPermission;
                }

                return hasPermission;
            }
        };

        let authorized = true;
        if (authorization) {
            if (typeof authorization === "function") {
                return authorization();
            }

            let requestedAuthorization;
            if (isArray(authorization)) {
                requestedAuthorization = authorization;
            } else if (typeof authorization === "string") {
                requestedAuthorization = [authorization];
            }

            let l = requestedAuthorization.length;
            let i = 0;
            while (authorized && i < l) {
                const accessPolicy = requestedAuthorization[i++];
                authorized = permissionFunc(accessPolicy);
                if (!authorized) {
                    const section = first(split(accessPolicy, "."));
                    if (section && section !== "") {
                        authorized = permissionFunc(section + ".full");
                    }
                }
            }
        } else {
            authorized = permissionFunc(null);
        }

        return authorized;
    }
}

function authorizationValid(authorization) {
    return !isNil(authorization) && authorization;
}

export default NakiRadioUserStore;
